import { useTranslations } from 'use-intl';
import { createFileRoute } from '@tanstack/react-router';
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { LoadingState } from '@leanin/ui';
import { z } from 'zod';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { returnPathSearchParam, withReturnPath } from '@/lib/return-path';

// `next` is the page a sign-in bounce was carrying (SsoButtons puts it on the
// callback URL). Clerk's callback only knows its own props — not the
// `redirectUrlComplete` the OAuth started with — so the outcomes that finish
// here forward it themselves.
const ssoCallbackSearchSchema = z.object({
  next: returnPathSearchParam,
});

export const Route = createFileRoute('/auth/sso-callback')({
  validateSearch: ssoCallbackSearchSchema.parse,
  component: SsoCallbackPage,
});

// Side-effect-only Clerk component: it has no DOM. Render a visible
// loading state alongside it so the user sees something while the OAuth
// handshake completes.
//
// The four URL props each handle a different post-redirect branch
// (Clerk routes to one of them based on the OAuth handshake outcome):
//
//   - signInFallback / signUpFallback → success path. Lands on
//     `/auth/finishing` which finalises the member row + onboarding gate.
//   - signInUrl / signUpUrl → Clerk decided the user can't proceed
//     (e.g. account doesn't exist for a sign-in attempt, or the OAuth
//     identity can't be linked to an existing user). Route back to the
//     canonical entry page so they can pick a different method.
//   - continueSignUpUrl → the OAuth round-trip returned with a partial
//     SignUp that still has `missing_requirements`. The biggest case in
//     production: invited signup where Google returned a DIFFERENT email
//     than the invitation. Without this, the partial signUp's error
//     never surfaces — finishing.tsx sees no user, falls into the 5s
//     hydration timer, and miscategorises the failure as an 'unknown'
//     error. Routing back to /auth/signup at least re-presents the form;
//     SignupForm does NOT yet inspect the partial signUp's
//     `verifications.externalAccount.error` to explain the mismatch.
function SsoCallbackPage() {
  const t = useTranslations('Auth');
  const { next } = Route.useSearch();
  const finishingUrl = withReturnPath('/auth/finishing', next);
  return (
    <AuthLayout
      kicker={t('ssoCallback.kicker')}
      headline={t('ssoCallback.headline')}
    >
      <LoadingState placement="page" label={t('ssoCallback.label')} />
      <AuthenticateWithRedirectCallback
        signInFallbackRedirectUrl={finishingUrl}
        signUpFallbackRedirectUrl={finishingUrl}
        signInUrl={withReturnPath('/sign-in', next)}
        signUpUrl="/auth/signup"
        continueSignUpUrl="/auth/signup"
      />
    </AuthLayout>
  );
}
